/* import __COLOCATED_TEMPLATE__ from './share-externally.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import { flatten } from 'underscore';
import type Report from 'embercom/models/reporting/custom/report';
import type IntlService from 'ember-intl/services/intl';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import type RegionService from 'embercom/services/region-service';
import ajax from 'embercom/lib/ajax';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import ENV from 'embercom/config/environment';
import { hasFeature } from 'embercom/helpers/has-feature';
interface Args {
  report: Report;
  isOpen: boolean;
  onClose: () => void;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

type _ChartType = {
  title: string;
  visualizationType: string;
};

const SIGNAL_FLEXIBLE_URL = '/ember/reporting/signal_flexible';
const EXTERNAL_SHARE_URL = '/ember/reporting/custom_reports/snapshot';
export default class ReportingCustomReportShareExternally extends Component<Signature> {
  @service declare intl: IntlService;
  @service analytics: $TSFixMe;
  @service declare reportingChartService: ReportingChartService;
  @service declare regionService: RegionService;
  @service declare appService: any;
  @service declare notificationsService: any;
  @service declare permissionsService: any;

  @tracked recipientEmails: string[] = [];
  @tracked emailSubject = '';
  @tracked personalMessage = '';
  @tracked generatedPublicLink: string | null = null;

  get report(): Report {
    return this.args.report;
  }

  get isGenerateButtonDisabled(): boolean {
    return this.recipientEmails.length === 0;
  }

  get app() {
    return this.appService.app;
  }

  get isGeneratingLink(): boolean {
    return taskFor(this.generateLinkTask).isRunning;
  }

  get reportContainsDataTable(): { containsDataTable: boolean; dataTables: string[] } {
    if (this.report.charts === undefined) {
      return { containsDataTable: false, dataTables: [] };
    }
    let dataTables = this.report.charts
      .filter((chart: _ChartType) => chart.visualizationType === 'data-table')
      .map((chart: _ChartType) => chart.title);
    return { containsDataTable: dataTables.length > 0, dataTables };
  }

  @action
  updateRecipients(emails: string[]): void {
    this.recipientEmails = emails;
  }

  @action
  updateSubject(event: Event): void {
    let target = event.target as HTMLInputElement;
    this.emailSubject = target.value;
  }

  @action
  updateMessage(event: Event): void {
    let target = event.target as HTMLTextAreaElement;
    this.personalMessage = target.value;
  }

  async fetchData(dataRequest: $TSFixMe) {
    return await ajax({
      url: SIGNAL_FLEXIBLE_URL,
      type: 'POST',
      data: JSON.stringify({
        app_id: this.app.id,
        ...(this.reportingChartService.syntheticDataUseCase
          ? { synthetic_data_use_case: this.reportingChartService.syntheticDataUseCase }
          : {}),
        ...dataRequest,
      }),
    });
  }

  @task({ drop: true })
  *generateLinkTask(): TaskGenerator<void> {
    try {
      // if has feature reporting-snapshot-chart-data-api, then we don't need to generate the snapshot responses, we
      // can just generate the share link without the snapshot responses
      let chartDataSnapshotGeneration = hasFeature(
        'reporting-snapshot-chart-data-api',
        this.appService,
      );

      if (chartDataSnapshotGeneration) {
        // For visibility while testing, please revert once we set Chart DATA API as deafault
        // eslint-disable-next-line no-console
        console.log('Snapshot Generated with Chart Data Api');
        let shareResponse = yield this.createExternalShareLink();
        this.handleShareLinkResponse(shareResponse);
        return;
      }

      let snapshotResponses = yield this.generateSnapshotResponses();
      let shareResponse = yield this.createExternalShareLink(snapshotResponses);
      this.handleShareLinkResponse(shareResponse);
    } catch (error) {
      this.handleGenerationError(error);
    }
  }

  private async generateSnapshotResponses(): Promise<any[]> {
    let requestSnapshots = this.report.snapshotFlexibleRequest();
    let snapshotPromises = this.createSnapshotPromises(requestSnapshots);
    let responses = await Promise.all(snapshotPromises);
    return flatten(responses);
  }

  private createSnapshotPromises(requestSnapshots: any[]): Promise<any>[] {
    return requestSnapshots.map(async (snapshot: any) => {
      let chart_id = snapshot.chart_id;
      let dataRequests = snapshot.requests;

      let dataRequestPromises = dataRequests.map((dataRequest: any) => this.fetchData(dataRequest));
      let responses = await Promise.all(dataRequestPromises);

      return {
        chart_id,
        responses,
      };
    });
  }

  private createExternalShareLink(snapshotResponses?: any[]): Promise<any> {
    let { id, updatedAt } = this.report;
    let updatedAtInSeconds = Math.floor(updatedAt.getTime() / 1000);

    let payload: Record<string, unknown> = {
      app_id: this.app.id,
      admin_id: this.app.currentAdmin.id,
      report_id: id,
      report_version: updatedAtInSeconds,
      synthetic_data_use_case: this.reportingChartService.syntheticDataUseCase,
    };

    if (Array.isArray(snapshotResponses) && snapshotResponses.length > 0) {
      payload.snapshot_responses = snapshotResponses;
    }

    return ajax({
      url: EXTERNAL_SHARE_URL,
      type: 'POST',
      data: JSON.stringify(payload),
    });
  }

  private handleShareLinkResponse(response: any): void {
    if (response && response.public_link) {
      let formattedLink = this.formatPublicLink(response.public_link);
      this.generatedPublicLink = formattedLink;

      this.notificationsService.notifyConfirmation(
        this.intl.t('reporting.custom-reports.report.share-externally-link-generated'),
      );
    } else {
      this.notificationsService.notifyError(
        this.intl.t('reporting.custom-reports.report.share-externally-generation-error'),
      );
    }
  }

  private handleGenerationError(error: any): void {
    let errorMessage =
      error.jqXHR?.status === 409
        ? this.intl.t('reporting.custom-reports.report.stale-report-error')
        : this.intl.t('reporting.custom-reports.report.save-error');
    this.notificationsService.notifyError(errorMessage);
  }

  @action
  generateLink(): void {
    if (this.permissionsService.currentAdminCan('can_export_csv')) {
      taskFor(this.generateLinkTask).perform();
    } else {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal('can_export_csv');
    }
  }

  private formatPublicLink(publicLinkId: string): string {
    let baseUrl = this.getPublicLinkBaseUrl();
    return `${baseUrl}/report-snapshot/${publicLinkId}`;
  }

  private getPublicLinkBaseUrl(): string {
    if (ENV.environment === 'development' || ENV.environment === 'test') {
      return 'http://intercom.test';
    }
    return this.regionService.appUrl(this.regionService.region);
  }

  @action
  generateAndShareLink(): void {
    if (this.permissionsService.currentAdminCan('can_export_csv')) {
      // Placeholder logic for link generation and email sharing
      let shareData = {
        reportId: this.report.id,
        reportTitle: this.report.titleForDisplay,
        recipients: this.recipientEmails,
        subject: this.emailSubject,
        message: this.personalMessage,
        timestamp: new Date().toISOString(),
      };

      this.analytics.track('external_sharing_link_generated', {
        report_id: shareData.reportId,
        recipient_count: shareData.recipients.length,
        has_subject: shareData.subject.length > 0,
        has_personal_message: shareData.message.length > 0,
      });

      // TODO: Implement actual API call to generate link and send emails
      // Example: yield this.externalSharingService.generateAndShare(shareData);
    } else {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal('can_export_csv');
    }
  }

  @action
  openHelpArticle(): void {
    safeWindowOpen('https://www.intercom.com/help/en/articles/12587881-share-reports-externally');

    this.analytics.track('external_sharing_help_opened', {
      report_id: this.report.id,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::Report::ShareExternally': typeof ReportingCustomReportShareExternally;
    'reporting/custom/report/share-externally': typeof ReportingCustomReportShareExternally;
  }
}
